import { useState, useReducer, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { toast } from 'sonner'
import { agentUrl, conversationUrl, type AgentId } from '../config/env'
import { ApiError, mutateJsonWithAuth, requireAuthToken } from '../lib/api'
import { conversationKeys } from '../lib/queryKeys'
import { HttpAgent } from '@ag-ui/client'
import { useSession } from '@clerk/clerk-react'
import type {
  ClarifyingAnswerPayload,
  MessageIntent,
  UserMessageDisplay,
} from '../types'
import { agentStreamReducer } from './useAgentStreamReducer'
import type { StreamingState } from './useAgentStreamReducer'
import { createStreamHandlers } from './useAgentStreamHandlers'
import { useRetry } from './useRetry'
import { classifyFetchError, NETWORK_ERROR_MESSAGE } from '../lib/fetchError'
import { getUiContextSnapshot, type UiContextSnapshot } from '../lib/uiContext'
import {
  chatAttachmentRefs,
  csvAttachmentRefs,
  isStorageBackedAttachment,
  type PendingAttachment,
} from '../lib/attachments'
import { uploadChatAttachment, uploadFile } from '../features/files/api'
import { chameleon } from '../lib/chameleon'
import { useMessageQueue } from '../stores/messageQueue'

export type { StreamingState }

interface UseAgentStreamOptions {
  agentId: AgentId
}

export interface SendOptions {
  attachments?: PendingAttachment[]
  /** Set only by the hook's own retry, which replays the first attempt's snapshot. */
  uiContext?: UiContextSnapshot
  intent?: MessageIntent
}

const MAX_AUTO_RETRIES = 2

function errorMessageFor(err: unknown, fallback: string): string {
  if (classifyFetchError(err) === 'network') return NETWORK_ERROR_MESSAGE
  return err instanceof Error ? err.message : fallback
}

export function useAgentStream(options: UseAgentStreamOptions) {
  const [events, dispatch] = useReducer(agentStreamReducer, [])
  const [eventsThreadId, setEventsThreadId] = useState<string | undefined>(
    undefined
  )
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
  })
  // Locally-submitted clarifying answers, keyed by tool-call id. Locks the card
  // to read-only immediately on submit, before the resumed turn's persisted
  // tool result lands on the next refetch.
  const [clarifyingAnswers, setClarifyingAnswers] = useState<
    Record<string, ClarifyingAnswerPayload>
  >({})
  // The last run ended in an error the retries could not clear. Lets the
  // queue hold instead of draining into a thread that just failed.
  const [runFailed, setRunFailed] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { session } = useSession()

  const { agentId } = options
  const navigate = useNavigate()
  const posthog = usePostHog()
  const queryClient = useQueryClient()

  // RUN_STARTED: the 'running' row now exists server-side. Refetching the
  // conversations list here is what arms its conditional poll (it only polls
  // while its data shows a running conversation), so the sidebar spinner
  // appears without waiting for a manual refresh.
  const onRunStarted = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: conversationKeys.all(agentId) })
  }, [queryClient, agentId])

  // The stream closed without RUN_FINISHED or RUN_ERROR: the run was stopped
  // from another tab, or died before the adapter started. Hand over to
  // polling so the persisted state and its status show.
  const handleEarlyClose = useCallback(
    (threadId: string | undefined) => {
      setStreamingState({ isStreaming: false })
      if (threadId) {
        queryClient.invalidateQueries({
          queryKey: conversationKeys.messagesAll(agentId, threadId),
        })
      }
    },
    [queryClient, agentId]
  )

  // The thread of the queued message in flight, if any. One that runs out of
  // retries goes back to its queue (useQueueDrain), so Retry resumes that
  // queue instead of sending a second copy.
  const queuedThreadRef = useRef<string | null>(null)

  const onRetryExhausted = useCallback(
    (errorMsg: string, retryFn: () => void) => {
      const queuedThread = queuedThreadRef.current
      setStreamingState({ isStreaming: false })
      setRunFailed(true)
      toast.error(errorMsg, {
        duration: Infinity,
        action: {
          label: 'Retry',
          onClick: queuedThread
            ? () => useMessageQueue.getState().resume(queuedThread)
            : retryFn,
        },
      })
    },
    []
  )

  const retry = useRetry({
    maxRetries: MAX_AUTO_RETRIES,
    onExhausted: onRetryExhausted,
  })

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const buildAgent = useCallback(
    async (threadId?: string) => {
      const token = await session?.getToken()
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      return new HttpAgent({
        url: agentUrl(agentId),
        threadId,
        headers: token
          ? {
              Authorization: token,
              'Content-Type': 'application/json',
              'X-User-Timezone': userTimezone,
            }
          : {
              'Content-Type': 'application/json',
              'X-User-Timezone': userTimezone,
            },
      })
    },
    [session, agentId]
  )

  const sendMessage = useCallback(
    async (
      messageText: string,
      threadId?: string,
      { attachments = [], uiContext, intent }: SendOptions = {}
    ) => {
      // An attachment with no text is a legitimate turn ("look at this").
      if (!messageText.trim() && attachments.length === 0) return

      // Cancel any pending retry from a previous attempt
      retry.cancel()
      setRunFailed(false)
      queuedThreadRef.current = intent === 'queued' ? (threadId ?? null) : null

      // Abort any in-flight stream and create a new controller for this call.
      // Capture in a local const so the await below can't swap it out.
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller
      const { signal } = controller

      const userEvent: UserMessageDisplay = {
        id: `user-${Date.now()}`,
        type: 'user_message',
        content: messageText,
        timestamp: new Date(),
        optimistic: true,
        attachments: attachments.map((a) => ({
          filename: a.filename,
          mediaType: a.mediaType,
          url: a.url,
        })),
        ...(intent ? { intent } : {}),
      }

      dispatch({ type: 'SET_EVENTS', events: [userEvent] })
      setEventsThreadId(threadId)
      setStreamingState({ isStreaming: true })

      // David-only: capture the UI snapshot ONCE for this logical message and
      // reuse it across auto-retries, so a tab switch during the retry delay
      // can't change what David thinks the user was looking at.
      const uiSnapshot =
        agentId === 'david' ? (uiContext ?? getUiContextSnapshot()) : undefined

      // Retry closure captures this call's messageText/threadId + the snapshot
      const retryThisMessage = () =>
        sendMessage(messageText, threadId, {
          attachments,
          uiContext: uiSnapshot,
          intent,
        })
      // Post-upload errors keep the default: every file ID is cached by then,
      // so a retry re-sends only references.
      const handleError = (errorMsg: string, retryable = true) => {
        if (!retryable) {
          retry.cancel()
          onRetryExhausted(errorMsg, retryThisMessage)
          return
        }
        retry.scheduleRetry(retryThisMessage, errorMsg)
      }

      try {
        if (attachments.length) {
          try {
            const uploadToken = await session?.getToken()
            if (!uploadToken)
              throw new Error('Authentication required to upload attachments')
            await Promise.all(
              attachments.map(async (attachment) => {
                // Keep the private storage ID on the captured attachment so a
                // retry references the same upload instead of making an
                // orphaned duplicate.
                if (attachment.fileId) return
                if (isStorageBackedAttachment(attachment)) {
                  const uploaded = await uploadFile(
                    uploadToken,
                    attachment.file
                  )
                  attachment.fileId = uploaded.file.id
                } else {
                  const uploaded = await uploadChatAttachment(
                    uploadToken,
                    attachment.file
                  )
                  attachment.fileId = uploaded.id
                }
              })
            )
          } catch (uploadErr) {
            if (signal.aborted) return 'aborted'
            // A deterministic rejection (bad type, over a cap) would re-upload
            // the full file on every auto-retry; only network blips retry.
            const retryable = classifyFetchError(uploadErr) === 'network'
            handleError(
              errorMessageFor(uploadErr, 'Failed to upload attachments'),
              retryable
            )
            // A queued message stays with the drain while its retry runs.
            if (retryable && intent === 'queued') return
            // 'failed', not a bare return: nothing was sent, so the caller has
            // to put the files back in the composer.
            return 'failed'
          }
        }
        const csvRefs = csvAttachmentRefs(attachments)
        const chatRefs = chatAttachmentRefs(attachments)
        const agent = await buildAgent(threadId)

        // Only send the new user message — backend loads conversation
        // history from DB, so sending full history here would cause
        // duplicate turns. Files ride as references in forwardedProps, never
        // as inline bytes.
        agent.addMessage({
          id: userEvent.id,
          role: 'user',
          content: messageText,
        })

        const handlers = createStreamHandlers(
          dispatch,
          setStreamingState,
          handleError,
          () => {
            retry.reset()
            // RUN_FINISHED is the only true success signal: runAgent also
            // resolves after a RUN_ERROR that scheduled a retry, and an
            // eventually-successful retry resolves into a detached timer.
            chameleon.track('Message Sent')
          },
          signal,
          onRunStarted
        )

        // David-only: attach the per-message UI snapshot so it knows which tabs
        // are open and what the user is viewing (captured once above).
        const forwardedProps = {
          ...(uiSnapshot ? { uiContext: uiSnapshot } : {}),
          ...(csvRefs.length ? { csvAttachments: csvRefs } : {}),
          ...(chatRefs.length ? { chatAttachments: chatRefs } : {}),
          ...(intent ? { intent } : {}),
        }
        await agent.runAgent(
          {
            abortController: controller,
            ...(Object.keys(forwardedProps).length ? { forwardedProps } : {}),
          },
          handlers
        )
        if (!signal.aborted && !handlers.sawTerminal) handleEarlyClose(threadId)
      } catch (err) {
        // Don't process errors from aborted requests
        if (signal.aborted) return 'aborted'

        console.error('Error in sendMessage:', err)

        const errorMessage = err instanceof Error ? err.message : String(err)
        const isAgentDisabled = errorMessage.includes('AGENT_DISABLED')
        const isConfigError = errorMessage.includes('CONFIGURATION_ERROR')

        if (isAgentDisabled || isConfigError) {
          toast.error('This agent is disabled.', { duration: 5000 })
          setStreamingState({ isStreaming: false })
          navigate('/', { replace: true })
          return
        }

        if (errorMessage.includes('RUN_IN_PROGRESS')) {
          // The server is still working on the previous message (a detached
          // run, or a Stop that has not landed yet). Re-sending would only 409
          // again, so drop the optimistic bubble and watch the run instead.
          retry.cancel()
          dispatch({ type: 'CLEAR_EVENTS' })
          setEventsThreadId(undefined)
          setStreamingState({ isStreaming: false })
          if (threadId) {
            queryClient.invalidateQueries({
              queryKey: conversationKeys.messagesAll(agentId, threadId),
            })
          }
          toast.info(
            'Still working on the previous message. Please wait for it to finish.'
          )
          // Nothing was sent, same as a failed upload.
          return 'failed'
        }

        const kind = classifyFetchError(err)
        posthog?.captureException(err, {
          context: 'agent_stream',
          agent_id: agentId,
          online: navigator.onLine,
          kind,
        })

        handleError(errorMessageFor(err, 'An error occurred'))
      }
      // Stop usually lands without throwing — the handlers just bail on the
      // signal — so callers learn about it here, not only from the catch.
      if (signal.aborted) return 'aborted'
    },
    [
      buildAgent,
      agentId,
      navigate,
      retry,
      posthog,
      onRetryExhausted,
      session,
      onRunStarted,
      queryClient,
      handleEarlyClose,
    ]
  )

  // Answer a pending clarifying-questions card. The structured answer rides in
  // forwardedProps (no new user message); the backend feeds it back as the
  // deferred tool result and the run resumes. We lock the card to read-only
  // immediately, then stream David's continuation into the same canvas.
  const submitClarifyingAnswer = useCallback(
    async (
      toolCallId: string,
      payload: ClarifyingAnswerPayload,
      threadId: string
    ) => {
      retry.cancel()
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller
      const { signal } = controller

      setClarifyingAnswers((prev) => ({ ...prev, [toolCallId]: payload }))
      dispatch({ type: 'SET_EVENTS', events: [] })
      setEventsThreadId(threadId)
      setStreamingState({ isStreaming: true })

      // A clarifying answer resolves a pending tool call server-side, so it must
      // NOT be auto-retried like a normal message: the first attempt already
      // serviced (and persisted) the deferred call, so re-submitting just hits
      // the "already answered" guard (409) — which is exactly the cascade we'd
      // otherwise loop on. On failure we surface it once and stop; the card
      // stays locked (optimistically set above) and the conversation refetches
      // when streaming ends, so the user can continue from the composer.
      const handleSubmitError = (errorMsg: string) => {
        if (signal.aborted) return
        setStreamingState({ isStreaming: false })
        // The answer already landed (or another tab/submit resolved it); the
        // card stays locked and the thread refetches, but say so instead of
        // leaving a submit that visibly did nothing.
        if (errorMsg.includes('CLARIFY_ALREADY_RESOLVED')) {
          toast.info('This question was already answered.')
          return
        }
        // The run failed before the answer was serviced server-side. Roll back
        // the optimistic lock so the card reverts to interactive instead of
        // showing answers David never actually received.
        setClarifyingAnswers((prev) => {
          const next = { ...prev }
          delete next[toolCallId]
          return next
        })
        toast.error(errorMsg)
      }

      try {
        const agent = await buildAgent(threadId)
        const handlers = createStreamHandlers(
          dispatch,
          setStreamingState,
          handleSubmitError,
          undefined,
          signal,
          onRunStarted
        )
        await agent.runAgent(
          {
            abortController: controller,
            forwardedProps: {
              clarifyingAnswer: { toolCallId, ...payload },
              // Resumed turn also carries the current UI snapshot (david only);
              // backend readers are key-matched so the two coexist.
              ...(agentId === 'david'
                ? { uiContext: getUiContextSnapshot() }
                : {}),
            },
          },
          handlers
        )
        if (!signal.aborted && !handlers.sawTerminal) handleEarlyClose(threadId)
      } catch (err) {
        if (signal.aborted) return
        console.error('Error in submitClarifyingAnswer:', err)
        const kind = classifyFetchError(err)
        handleSubmitError(
          kind === 'network'
            ? NETWORK_ERROR_MESSAGE
            : err instanceof Error
              ? err.message
              : 'An error occurred'
        )
      }
    },
    [agentId, buildAgent, retry, onRunStarted, handleEarlyClose]
  )

  // Inject a message into the live run's next model request. The bubble is
  // added client-side: the AG-UI stream carries no event for a drained steer.
  // Anything short of an acknowledged steer puts the text in the queue, which
  // is the one send path: the drain sends it once no run is active. A 404
  // means no run on this instance (ended, or another instance owns it); a 409
  // means the run is alive but past the point where it can take one (saving,
  // titling, billing). Both are silent; any other error also toasts.
  // queueAt puts the text back where a promoted queue item came from.
  const steer = useCallback(
    async (messageText: string, threadId: string, queueAt?: number) => {
      if (!messageText.trim()) return
      try {
        const token = session
          ? await requireAuthToken(() => session.getToken())
          : ''
        await mutateJsonWithAuth({
          token,
          url: `${conversationUrl(agentId, threadId)}/steer`,
          method: 'POST',
          body: { content: messageText },
          errorMessage: 'Failed to steer the run',
        })
      } catch (err) {
        const { enqueue, reorder } = useMessageQueue.getState()
        const queued = enqueue(threadId, messageText)
        if (queueAt !== undefined) reorder(threadId, queued.id, queueAt)
        if (
          err instanceof ApiError &&
          (err.status === 404 || err.status === 409)
        ) {
          return 'queued'
        }
        toast.error(errorMessageFor(err, 'Failed to steer the run'))
        return 'failed'
      }
      if (eventsThreadId === threadId) {
        const userEvent: UserMessageDisplay = {
          id: `user-${Date.now()}`,
          type: 'user_message',
          content: messageText,
          timestamp: new Date(),
          intent: 'steer',
          optimistic: true,
        }
        dispatch({ type: 'ADD_EVENT', event: userEvent })
      }
      return 'enqueued'
    },
    [eventsThreadId, session, agentId]
  )

  const clearEvents = useCallback(() => {
    dispatch({ type: 'CLEAR_EVENTS' })
    setEventsThreadId(undefined)
    setClarifyingAnswers({})
  }, [])

  // Stop watching the current stream WITHOUT stopping the server-side run —
  // the run is detached and keeps working. Used when switching conversations.
  const detach = useCallback(() => {
    retry.cancel()
    abortControllerRef.current?.abort()
    setStreamingState({ isStreaming: false })
  }, [retry])

  // Stop an in-flight run. The run is detached from the SSE response
  // server-side, so aborting the fetch only stops watching it — the cancel
  // endpoint is what actually stops the run. threadId covers the reload case,
  // where the run is still working but this tab has no local stream state.
  const stop = useCallback(
    (threadId?: string) => {
      detach()
      const target = threadId ?? eventsThreadId
      if (!target) return
      void (async () => {
        try {
          const token = session
            ? await requireAuthToken(() => session.getToken())
            : ''
          await mutateJsonWithAuth({
            token,
            url: `${conversationUrl(agentId, target)}/cancel`,
            method: 'POST',
            errorMessage: 'Failed to stop the run',
          })
        } catch {
          // Best-effort: the run may already be over, or live on another
          // instance — polling settles the status either way.
        }
      })()
    },
    [detach, eventsThreadId, session, agentId]
  )

  // Drop the local stream state for a thread that no longer exists (e.g. a
  // deleted conversation): abort it if it's still streaming, then clear its
  // events — but only when the events on screen actually belong to it. Owning
  // this here keeps callers from reaching into stop()/clearEvents()/
  // eventsThreadId to hand-roll the "dead thread" rule.
  const discardThread = useCallback(
    (threadId: string) => {
      useMessageQueue.getState().clear(threadId)
      if (eventsThreadId !== threadId) return
      // Local cleanup only: the server's delete route stops the run itself.
      if (streamingState.isStreaming) detach()
      clearEvents()
    },
    [eventsThreadId, streamingState.isStreaming, detach, clearEvents]
  )

  return {
    events,
    eventsThreadId,
    streamingState,
    runFailed,
    sendMessage,
    steer,
    submitClarifyingAnswer,
    clarifyingAnswers,
    stop,
    detach,
    clearEvents,
    discardThread,
  }
}
